import { Injectable } from "@angular/core";
import { Tool } from "@trimble-agentic-external-npm-local/agentic-platform-sdk-iframe-typescript";
import { FieldTypeOption } from 'src/app/shared/template-services/field';
import { FeatureFilterStreamService } from "../../current-features/feature-filter-stream.service";
import { AttrValues, FeatureFilter } from '../../feature-filter/feature-filter';
import { DateUtils } from "../../utility/date-utils";

@Injectable({ providedIn: 'root' })
export class ApplyFilterTool {

    constructor(
        private featureFilterStream: FeatureFilterStreamService
    ) {}
    
    readonly definition: Tool = {
        name: 'apply_filters',
        description: 'Apply map filters to limit the features being displayed. Use this when the user asks to show only a specific group of features.',
        parameters: {
            type: 'object',
            properties: {
                userIds: {
                    type: 'array',
                    items: { type: 'string' },
                    description: 'The ID(s) of the users to include'
                },
                fromDate: {
                    type: 'string',
                    description: 'The starting date of the period to filter from'
                },
                toDate: {
                    type: 'string',
                    description: 'The end date of the period to filter from'
                },
                unassignedUser: {
                    type: 'boolean',
                    description: 'Set true to filter by imported/unassigned features'
                },
                selectedUsersInfo: {
                    type: 'array',
                    items: { type: 'string' },
                    description: 'User display info for filtering features migrated from insphere'
                },
                isExported: {
                    type: 'boolean',
                    description: 'Filter to exported features'
                },
                isNotExported: {
                    type: 'boolean',
                    description: 'Filter to not-exported features'
                },
                isPostProcessingDone: {
                    type: 'boolean',
                    description: 'Filter by offline GNSS correction status: done'
                },
                isPostProcessingPending: {
                    type: 'boolean',
                    description: 'Filter by offline GNSS correction status: pending'
                },
                isPostProcessingFailed: {
                    type: 'boolean',
                    description: 'Filter by offline GNSS correction status: failed'
                },
                isPostProcessingUnprocessed: {
                    type: 'boolean',
                    description: 'Filter by offline GNSS correction status: unprocessed'
                },
                selectedLayer: {
                    type: 'string',
                    description: 'Layer ID to filter by attribute values'
                },
                selectedField: {
                    type: 'string',
                    description: 'Template field UUID for attribute filter'
                },
                selectedFieldTypeOption: {
                    type: 'object',
                    description: 'Field type metadata for the selected attribute (optional, can be derived from layer/field)'
                },
                attrValues: {
                    description: 'Attribute filter value: string, number, boolean, date range { from, to }, string[], or multi-select { [key]: boolean }'
                },
                availableCustomTags: {
                    type: 'array',
                    items: { type: 'string' },
                    description: 'Available custom tags in the workspace'
                },
                customTags: {
                    type: 'array',
                    items: { type: 'string' },
                    description: 'Filter to features with these custom tags'
                },
                noCustomTags: {
                    type: 'boolean',
                    description: 'Filter to features with no custom tags'
                }
            }
        }
    }

    /**
     * Apply map filters to limit the features being displayed.
     * Use this when the user asks to show only a specific group of features.
     */
    async execute(params: {
        userIds?: string[];
        fromDate?: string | Date;
        toDate?: string | Date;
        unassignedUser?: boolean;
        selectedUsersInfo?: string[];
        isExported?: boolean;
        isNotExported?: boolean;
        isPostProcessingDone?: boolean;
        isPostProcessingPending?: boolean;
        isPostProcessingFailed?: boolean;
        isPostProcessingUnprocessed?: boolean;
        selectedLayer?: string;
        selectedField?: string;
        selectedFieldTypeOption?: FieldTypeOption;
        attrValues?: AttrValues;
        availableCustomTags?: string[];
        customTags?: string[];
        noCustomTags?: boolean;
    } = {}): Promise<string> {
        try {
            const {
                userIds = [],
                fromDate,
                toDate,
                unassignedUser = false,
                selectedUsersInfo = [],
                isExported = false,
                isNotExported = false,
                isPostProcessingDone = false,
                isPostProcessingPending = false,
                isPostProcessingFailed = false,
                isPostProcessingUnprocessed = false,
                selectedLayer = null,
                selectedField = null,
                selectedFieldTypeOption = null,
                attrValues,
                availableCustomTags = [],
                customTags = [],
                noCustomTags = false
            } = params;

            const filter = new FeatureFilter();
            filter.userIds = userIds;
            filter.fromDate = fromDate
                ? (DateUtils.startOfDay(fromDate) as Date)
                : null;
            filter.toDate = toDate ? (DateUtils.startOfDay(toDate) as Date) : null;
            filter.unassignedUser = unassignedUser;
            filter.selectedUsersInfo = selectedUsersInfo;
            filter.isExported = isExported;
            filter.isNotExported = isNotExported;
            filter.isPostProcessingDone = isPostProcessingDone;
            filter.isPostProcessingPending = isPostProcessingPending;
            filter.isPostProcessingFailed = isPostProcessingFailed;
            filter.isPostProcessingUnprocessed = isPostProcessingUnprocessed;
            filter.selectedLayer = selectedLayer;
            filter.selectedField = selectedField;
            filter.selectedFieldTypeOption = selectedFieldTypeOption;
            filter.attrValues = attrValues;
            filter.availableCustomTags = availableCustomTags;
            filter.customTags = customTags;
            filter.noCustomTags = noCustomTags;

            this.featureFilterStream.activeFilter = filter;

            return JSON.stringify({ status: 'ok', message: 'Filters applied successfully' });
        } catch (error) {
            return JSON.stringify({ status: 'error', message: 'Failed to apply filters' });
        }
    };
}